import React, { useEffect, useState } from 'react';
import {
    FontAwesomeIcon,
} from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import styled from '@emotion/styled';
import Select, { components, CSSObjectWithLabel } from 'react-select'
import makeAnimated from 'react-select/animated';
import Hit from "./Hit";
import Spinner from './Spinner';
import DatasetHit from 'types/DatasetHit';
import { useServices } from 'services';

const sortTypes = [
    {
        name: 'Date',
        sort: (a: DatasetHit, b: DatasetHit) => {
            return b.metadata.publication_date.localeCompare(a.metadata.publication_date)
        },
    },
    {
        name: 'Title',
        sort: (a: DatasetHit, b: DatasetHit) => {
            return b.metadata.title.localeCompare(a.metadata.title, "en", { sensitivity: 'base' })
        },
    },
];

const animatedComponents = makeAnimated();

const Wrapper = styled.div`
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    @media (width >= 48rem) {
        flex-direction: row;
        align-items: flex-end;
    }
    > div:first-of-type {
        display: flex;
        flex-direction: column;
        gap-x: 1.25rem;
        gap-y: 0.5rem;
        flex: 1;
        @media (width >= 48rem) {
            flex-direction: row;
            align-items: flex-end;
        }
    }
    button:hover {
        text-decoration: underline;
        text-underline-offset: 2px;
    }
`;

interface Filter {
    label: string,
    value: string,
}

interface DataProductProps {
    projectSearchKey: string,
}

const DataProducts = ({ projectSearchKey }: DataProductProps): React.ReactElement => {

    const { envService } = useServices();

    const [fetchingData, setFetchingData] = useState<boolean>(true);
    const [sortDescending, setSortDescending] = useState<boolean>(true);
    const [sortType, setSortType] = useState<number>(0);
    const [hits, setHits] = useState<DatasetHit[]>([]);
    const [filteredHits, setFilteredHits] = useState<DatasetHit[]>(hits);

    const [filters, setFilters] = useState<Filter[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<Filter[]>([]);

    useEffect(() => {
        const base = envService.getValue('DATA_REPO_URL');
        const params = new URLSearchParams({
            msdlive_project: projectSearchKey,
            size: '1000',
            allversions: 'false',
        });
        const url = `${base}/api/records?${params.toString()}`;

        fetch(url, {
            headers: {
                "Accept": "application/vnd.inveniordm.v1+json",
            }
        })
            .then(r => r.json())
            .then(r => {
                setHits(r?.hits?.hits ?? []);
                const themes = r?.hits?.hits?.reduce(
                    (f: string[], h: DatasetHit) => Array.from(
                        new Set([...f, ...h?.metadata?.msdlive_science_themes?.map(t => t.science_theme) ?? []])
                    ),
                    []
                ) ?? [];
                themes.sort();
                setFilters(themes.map((t: string) => ({ label: t, value: t })));
            })
            .catch(e => { })
            .finally(() => {
                setFetchingData(false);
            });
    }, [projectSearchKey, envService]);

    useEffect(() => {
        if (hits.length > 0) {
            const filteredData = hits.filter(
                d => selectedFilters.length === 0 ? true : selectedFilters.map(f => f.value).some(
                    v => d?.metadata?.msdlive_science_themes?.map(t => t.science_theme).includes(v)
                )
            );
            const sortedData = sortDescending
                ? filteredData.slice().sort(sortTypes[sortType].sort)
                : filteredData.slice().sort(sortTypes[sortType].sort).reverse();
            setFilteredHits(sortedData);
        }
    }, [hits, selectedFilters, sortType, sortDescending]);

    const setSort = (i: number): void => {
        if (sortType === i) {
            setSortDescending(!sortDescending);
        }
        else {
            setSortType(i);
            setSortDescending(true);
        }
    };

    return (
        <>
            <Wrapper>
                <div>
                    <h3
                        style={{
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            color: "#364153",
                            textTransform: "uppercase",
                            flexShrink: "0",
                            margin: "0",
                        }}
                    >
                        Filter by Science Theme(s):
                    </h3>
                    <div
                        style={{
                            maxWidth: '24rem',
                            width: '100%',
                            marginLeft: '0.5rem'
                        }}
                    >
                        <Select
                            isMulti
                            options={filters}
                            onChange={(newValue) => {
                                setSelectedFilters(newValue as Filter[]);
                            }}
                            components={{
                                ...animatedComponents,
                                Option: props => props.isDisabled ? null : (
                                    <div
                                        {...props.innerProps}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '0.25rem',
                                            width: '100%',
                                            margin: '0',
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        <components.Option {...props} />
                                    </div>
                                ),
                            }}
                            placeholder="Select themes..."
                            styles={{
                                control: (s) => ({ ...s, width: '100%', padding: '0', minHeight: '1.5rem', cursor: 'pointer' } as unknown as CSSObjectWithLabel),
                                dropdownIndicator: (s) => ({ ...s, padding: '0 0.25rem' } as unknown as CSSObjectWithLabel),
                                clearIndicator: (s) => ({ ...s, padding: '0 0.25rem' } as unknown as CSSObjectWithLabel),
                                valueContainer: (s) => ({ ...s, padding: '0' } as unknown as CSSObjectWithLabel),
                                input: (s) => ({ ...s, padding: '0 0.25rem', margin: '0' } as unknown as CSSObjectWithLabel),
                                placeholder: (s) => ({ ...s, margin: '0 0.25rem', fontSize: '0.75rem' } as unknown as CSSObjectWithLabel),
                                multiValueLabel: (s) => ({ ...s, padding: '0.125rem', fontSize: '0.75rem' } as unknown as CSSObjectWithLabel),
                                option: (s) => ({ ...s, padding: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' } as unknown as CSSObjectWithLabel),
                            }}
                        />
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1rem",
                        flexShrink: "0",
                    }}
                >
                    <h3
                        style={{
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            color: "#364153",
                            textTransform: "uppercase",
                            flexShrink: "0",
                            margin: "0",
                        }}
                    >
                        Sort by:
                    </h3>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: "1rem",
                        }}
                    >
                        {sortTypes.map((s, i) => (
                            <button
                                key={i}
                                style={{
                                    textTransform: "uppercase",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    fontSize: "0.75rem",
                                    fontWeight: "300",
                                    lineHeight: "1.25",
                                    border: "none",
                                    background: "none",
                                    color: "#1b75bb",
                                    cursor: "pointer",
                                }}
                                onClick={() => setSort(i)}
                            >
                                {s.name}
                                <FontAwesomeIcon
                                    style={{
                                        color: i === sortType ? '#1b75bb' : 'white',
                                        marginLeft: "2px",
                                        marginBottom: "2px"
                                    }}
                                    icon={sortDescending ? faArrowDown : faArrowUp}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            </Wrapper>
            <div>
                {fetchingData
                    ? (
                        <div
                            style={{
                                height: '16rem',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Spinner />
                        </div>
                    )
                    : (
                        filteredHits.length === 0
                            ? (
                                <div
                                    style={{
                                        height: '16rem',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        justifyContent: 'center',
                                        padding: '2rem',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '1rem',
                                            color: '#c3c3c3',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        No results.
                                    </span>
                                </div>
                            )
                            : filteredHits.map((h, i) => (
                                <Hit key={i} hit={h} />
                            ))
                    )
                }
            </div>
        </>
    );
}

export default DataProducts;
